import { ComponentProps, useCallback, useEffect, useRef, useState } from "react";
import { View } from "react-native";
import { PROFILE_PHOTO_NUM_FACES } from "src/api/featureFlags";
import Header from "src/components/Header";
import ImageCropper, { ImageCropperHandler } from "src/components/ImageCropper";
import { FetchImageUriResult } from "src/components/ImageCropper/helpers/fetchImageUri/types";
import ProfilePhotoQualityHint from "src/components/ProfilePictureUploadWrapper/components/ProfilePhotoQualityHint";
import Touchable from "src/components/Touchable";
import useStyle from "src/hooks/useStyle";
import { useInsideModal } from "src/modals";
import GlobalModal from "src/modals/configs/GlobalModal";
import { color } from "src/styles";
import { radiusMedium } from "src/styles/radius";
import { paddingXXL } from "src/styles/spacing";
import { useSafeArea } from "src/swsh-native";
import { useImageCropModalDimensions } from "./ImageCropModalSkeleton";

const ImageCropModal = () => {
	const { props, closeSelf } = useInsideModal<GlobalModal["ImageCropModal"]>();
	const cropperRef = useRef<ImageCropperHandler>(null);
	const { imageUri, renderCropHint, onComplete, onCancel, onError } = props;

	const [renderCropHintProps, setRenderCropHintProps] = useState<null | ComponentProps<
		typeof ProfilePhotoQualityHint
	>>(null);

	const handleComplete = useCallback(async () => {
		try {
			const cropped = await cropperRef.current?.getCroppedImage();
			const completionResult = cropped?.uri ?? (await imageUri);
			if (!completionResult) throw new Error("No image available");
			await onComplete?.(completionResult);
		} catch (err) {
			onError?.(err as Error);
		} finally {
			closeSelf();
		}
	}, [closeSelf, imageUri, onComplete, onError]);

	const handleImageLoad = useCallback((result: FetchImageUriResult) => {
		if (result) {
			setRenderCropHintProps({
				uri: result.uri,
				imageHeight: result.height,
				imageWidth: result.width,
			});
		}
	}, []);

	const handleCancel = useCallback(() => {
		try {
			onCancel?.();
		} catch (err) {
			onError?.(err as Error);
		} finally {
			closeSelf();
		}
	}, [closeSelf, onCancel, onError]);

	const cropDimensions = useImageCropModalDimensions();
	const [cropperSize, setCropperSize] = useState<{
		width: number;
		height: number;
	} | null>(null);
	const safeArea = useSafeArea();
	const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
	useEffect(() => {
		(async () => {
			try {
				const resolvedImageUri = await imageUri;
				if (!resolvedImageUri) {
					handleCancel();
				}
				setResolvedSrc(resolvedImageUri);
			} catch (err) {
				handleCancel();
			}
		})();
	}, [handleCancel, imageUri]);
	const { theme } = useStyle();
	if (!resolvedSrc) return null;
	return (
		<View
			style={{
				flex: 1,
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Touchable
				animation="None"
				onPress={handleCancel}
				containerStyle={{
					width: "100%",
					height: "100%",
				}}
			/>

			<View
				style={[
					cropDimensions,
					{
						position: "absolute",
						paddingTop: safeArea.top,
						paddingBottom: safeArea.bottom,
						backgroundColor: color.BackgroundModal[theme],
						borderRadius: radiusMedium,
						overflow: "hidden",
					},
				]}
			>
				<Header
					completeVariant="Done"
					onComplete={handleComplete}
					exitVariant="Replace"
					onExit={handleCancel}
					variant="Modal"
					keyboardControls
					autoLoadingForOnComplete
					// [ENG-2786] TODO: update Header component instead to match Figma component
					style={{ paddingTop: paddingXXL, paddingBottom: paddingXXL }}
				>
					Crop
				</Header>
				<View
					onLayout={(event) => {
						const { width, height } = event.nativeEvent.layout;
						setCropperSize({ width, height });
					}}
					style={{
						flex: 1,
						overflow: "hidden",
					}}
				>
					{!!cropperSize && (
						<ImageCropper
							ref={cropperRef}
							width={cropperSize.width}
							height={cropperSize.height}
							src={resolvedSrc}
							onImageLoad={handleImageLoad}
							onImageLoadError={handleCancel}
						/>
					)}
				</View>
				{PROFILE_PHOTO_NUM_FACES &&
					renderCropHint &&
					renderCropHintProps &&
					renderCropHint(renderCropHintProps)}
			</View>
		</View>
	);
};
export default ImageCropModal;
